'use client';

import { useEffect, useRef, useState } from 'react';
import { Link } from '@/i18n/routing';
import { usePublicPathname } from '@/lib/use-public-pathname';
import { useTranslations } from 'next-intl';
import { ThemeSwitcher } from '@/components/ui/theme-switcher';
import { AccountMenu } from '@/components/layout/account-menu';
import { DATAHUNTER_HOME } from '@/lib/account-access';
import { DhLogo } from '@/components/ui/dh-logo';
import { TOOL_GROUPS, toolByCard } from '@/lib/dh-tool-groups';
import { localesWithPosts } from '@/lib/posts';
import { ChevronDown, Menu, X } from 'lucide-react';

/**
 * Шапка DataHunter.
 *
 * Отдельная от шапки INGDG: у продукта свой знак, своё меню и своя
 * аудитория. Общий компонент с флагом продукта означал бы ветвление
 * внутри каждой строки разметки.
 *
 * Инструменты по-прежнему работают без учётной записи, но место для
 * входа в шапке есть: без него к своим файлам на этом домене было бы
 * не попасть иначе как по набранному вручную адресу.
 *
 * Инструменты сгруппированы, а не перечислены подряд. Одиннадцать
 * ссылок в строку читаются как список, в котором ничего не найти:
 * глазу не за что зацепиться, и чем больше инструментов, тем хуже —
 * на узком ноутбуке ряд уже подходил к краю. Группы те же, что на
 * главной (`TOOL_GROUPS`), и берутся из общего источника: два разных
 * разбиения на одном сайте путали бы сильнее, чем отсутствие всякого.
 */
export function DhHeader({ locale }: { locale: string }) {
  const t = useTranslations('dh_layout');
  const tHome = useTranslations('dh_home');
  const tBlog = useTranslations('dh_blog');

  /**
   * Ссылка на разборы только там, где они написаны.
   *
   * На остальных языках она вела на страницу с одной строчкой «первые
   * разборы уже готовятся»: переход ради того, чтобы узнать, что
   * смотреть нечего.
   *
   * Язык приходит сверху, а не из адреса: шапка клиентская, а путь без
   * префикса у языка по умолчанию неотличим от пути без языка вообще.
   */
  const hasPosts = localesWithPosts('datahunter').includes(locale as never);
  const [open, setOpen] = useState(false);
  /** Какая группа раскрыта в широком меню. Одновременно только одна. */
  const [openGroup, setOpenGroup] = useState<string | null>(null);
  const pathname = usePublicPathname();
  const navRef = useRef<HTMLElement | null>(null);

  /**
   * Раскрытая группа закрывается при уходе на другую страницу.
   *
   * Переход внутри одного приложения не перезагружает документ, и
   * без этого меню оставалось бы висеть поверх новой страницы.
   */
  useEffect(() => {
    setOpenGroup(null);
    setOpen(false);
  }, [pathname]);

  /**
   * Escape и щелчок мимо меню закрывают его.
   *
   * Без этого раскрытый список нельзя убрать, не выбрав пункт: он
   * перекрывает начало страницы, и человек оказывается в ловушке
   * собственного любопытства.
   */
  useEffect(() => {
    if (!openGroup) return;

    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpenGroup(null);
    };
    const onClick = (event: MouseEvent) => {
      if (!navRef.current?.contains(event.target as Node)) setOpenGroup(null);
    };

    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onClick);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onClick);
    };
  }, [openGroup]);

  /** Инструменты группы с готовыми подписями. */
  const toolsOf = (group: (typeof TOOL_GROUPS)[number]) =>
    group.tools
      .map((card) => toolByCard(card))
      .filter((tool): tool is NonNullable<typeof tool> => Boolean(tool));

  /** Группа, внутри которой лежит открытая страница. */
  const currentGroup = TOOL_GROUPS.find((group) =>
    toolsOf(group).some((tool) => tool.href === pathname),
  );

  return (
    <header className="sticky top-0 z-50 border-b border-[var(--color-border)] bg-[var(--color-bg)]/80 backdrop-blur-lg relative">
      <div className="max-w-[1280px] mx-auto px-6 h-16 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={() => {
              setOpen(!open);
            }}
            className="md:hidden w-9 h-9 flex items-center justify-center rounded-lg border border-[var(--color-border)] hover:border-[var(--color-text-muted)] transition-colors"
            aria-label={t('menu')}
            aria-expanded={open}
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>

          <Link href="/" className="flex items-center gap-2.5 font-bold text-lg">
            <DhLogo className="w-7 h-7 text-[var(--color-text)]" />
            <span className="tracking-tight">DataHunter</span>
          </Link>
        </div>

        <nav ref={navRef} className="hidden md:flex items-center gap-2 text-sm">
          {TOOL_GROUPS.map((group) => {
            const tools = toolsOf(group);
            const expanded = openGroup === group.id;
            const active = currentGroup?.id === group.id;

            return (
              <div key={group.id} className="relative">
                <button
                  type="button"
                  onClick={() => {
                    setOpenGroup(expanded ? null : group.id);
                  }}
                  aria-expanded={expanded}
                  aria-haspopup="true"
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-lg transition-colors ${
                    active || expanded
                      ? 'text-[var(--color-text)] font-medium'
                      : 'text-[var(--color-text-muted)] hover:text-[var(--color-accent-400)]'
                  }`}
                >
                  {tHome(`group_${group.id}`)}
                  <ChevronDown
                    className={`w-3.5 h-3.5 transition-transform ${expanded ? 'rotate-180' : ''}`}
                  />
                </button>

                {/*
                  Список отрисован всегда и прячется стилем, а не
                  условием в разметке.
                 
                  Условный рендер убирал ссылки из отданного HTML: в
                  шапке каждой страницы не оставалось ни одной ссылки на
                  инструмент, они появлялись только после запуска JS.
                  Так теряется и перелинковка для поисковика, и
                  работоспособность без скриптов. `display: none`
                  одновременно убирает скрытые пункты из обхода табом.
                */}
                <div
                  className={`absolute left-0 top-full mt-1 min-w-[220px] rounded-xl border border-[var(--color-border)] bg-[var(--color-bg)] shadow-xl p-2 ${
                    expanded ? 'block' : 'hidden'
                  }`}
                >
                  {tools.map((tool) => (
                    <Link
                      key={tool.href}
                      href={tool.href}
                      // Текущий раздел подсвечен: человек с двумя
                      // открытыми инструментами иначе теряет, в каком
                      // из них находится.
                      aria-current={pathname === tool.href ? 'page' : undefined}
                      className={`block px-3 py-2 rounded-lg transition-colors ${
                        pathname === tool.href
                          ? 'text-[var(--color-text)] font-medium bg-[var(--color-bg-elevated)]'
                          : 'text-[var(--color-text-muted)] hover:text-[var(--color-text)] hover:bg-[var(--color-bg-elevated)]'
                      }`}
                    >
                      {t(tool.label)}
                    </Link>
                  ))}
                </div>
              </div>
            );
          })}

          {/* Разборы стоят рядом с группами, а не внутри одной из
              них: это не инструмент, а ответ на вопрос, с которым человек
              пришёл до того, как узнал о существовании инструмента. */}
          {hasPosts && (
            <Link
              href="/blog"
              aria-current={pathname === '/blog' ? 'page' : undefined}
              className={`px-3 py-2 rounded-lg transition-colors ${
                pathname.startsWith('/blog')
                  ? 'text-[var(--color-text)] font-medium'
                  : 'text-[var(--color-text-muted)] hover:text-[var(--color-accent-400)]'
              }`}
            >
              {tBlog('title')}
            </Link>
          )}
        </nav>

        <div className="flex items-center gap-3">
          <ThemeSwitcher />
          {/* Кабинет общий с INGDG, но на этом домене его главная
              закрыта, поэтому ведём сразу к файлам. */}
          <AccountMenu home={DATAHUNTER_HOME} />
        </div>
      </div>

      {/*
        Тоже отрисовано всегда и спрятано стилем: на широком экране это
        меню не видно, но его ссылки остаются в HTML. Именно они дают
        перелинковку страниц инструментов, которую условный рендер
        забирал целиком.
      */}
      <div
        className={`md:hidden absolute top-full left-0 right-0 z-40 border-t border-[var(--color-border)] bg-[var(--color-bg)] shadow-2xl max-h-[calc(100vh-4rem)] overflow-y-auto ${
          open ? 'block' : 'hidden'
        }`}
      >
        {/* На узком экране группы раскрыты сразу, без вложенных
              нажатий: список и так листается, а прятать одиннадцать
              ссылок за тремя дополнительными касаниями значит сделать
              их дальше, а не ближе. */}
        <nav className="flex flex-col px-6 py-4 gap-5">
          {TOOL_GROUPS.map((group) => (
            <div key={group.id}>
              <p className="px-3 pb-1.5 text-xs uppercase tracking-wide text-[var(--color-text-muted)]">
                {tHome(`group_${group.id}`)}
              </p>
              <div className="flex flex-col gap-1">
                {toolsOf(group).map((tool) => (
                  <Link
                    key={tool.href}
                    href={tool.href}
                    onClick={() => {
                      setOpen(false);
                    }}
                    aria-current={pathname === tool.href ? 'page' : undefined}
                    className="px-3 py-2 rounded-lg hover:bg-[var(--color-bg-elevated)] text-[var(--color-text)] transition-colors"
                  >
                    {t(tool.label)}
                  </Link>
                ))}
              </div>
            </div>
          ))}

          {hasPosts && (
            <Link
              href="/blog"
              onClick={() => {
                setOpen(false);
              }}
              aria-current={pathname === '/blog' ? 'page' : undefined}
              className="px-3 py-2 rounded-lg hover:bg-[var(--color-bg-elevated)] text-[var(--color-text)] transition-colors"
            >
              {tBlog('title')}
            </Link>
          )}
        </nav>
      </div>
    </header>
  );
}
